import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '@/lib/apiClient'
import { quoteBusinessTypes } from '@/lib/quotes'
import { Button } from '@/components/ui/Button'
import { Notice } from '@/components/ui/Feedback'
import { NativeSelect } from '@/components/ui/NativeSelect'

type Fields = { fullName: string; email: string; phone: string; businessType: string; businessTypeOther: string; website: string }
const empty: Fields = { fullName: '', email: '', phone: '', businessType: '', businessTypeOther: '', website: '' }

function problems(fields: Fields): Partial<Record<keyof Fields, string>> {
  const found: Partial<Record<keyof Fields, string>> = {}
  if (fields.fullName.trim().length < 3) found.fullName = 'Escribe tu nombre completo.'
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email.trim())) found.email = 'Escribe un correo electrónico válido.'
  const digits = fields.phone.replace(/\D/g, '').length
  if (digits < 10 || digits > 15) found.phone = 'Escribe un teléfono de 10 dígitos, o con lada internacional.'
  if (!fields.businessType) found.businessType = 'Elige el giro de tu negocio.'
  if (fields.businessType === 'OTHER' && !fields.businessTypeOther.trim()) found.businessTypeOther = 'Escribe cuál es el giro de tu negocio.'
  return found
}

/**
 * The home page's quote request, always open beside the headline: who asks, how to reach them and what their business
 * does; the administrator calls back. Each field is named by its hint; its label stays for screen readers. `focusOnArrival` puts the cursor in its first field, for a campaign link.
 */
export function QuotePanel({ focusOnArrival = false }: { focusOnArrival?: boolean }) {
  const [fields, setFields] = useState<Fields>(empty)
  const [submitted, setSubmitted] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const requestKey = useRef(crypto.randomUUID())
  const titleId = useId()
  const found = submitted ? problems(fields) : {}

  useEffect(() => {
    if (focusOnArrival) formRef.current?.querySelector<HTMLElement>('input, select')?.focus()
  }, [focusOnArrival])

  function change(field: keyof Fields, value: string) {
    setFields((current) => ({ ...current, [field]: value }))
  }

  function another() {
    setSent(false); setFields(empty); setSubmitted(false); setError('')
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    setSubmitted(true); setError('')
    if (Object.keys(problems(fields)).length > 0) {
      requestAnimationFrame(() => formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus())
      return
    }
    setSending(true)
    try {
      await api.post('/quotes', {
        fullName: fields.fullName.trim(), email: fields.email.trim(), phone: fields.phone.trim(), businessType: fields.businessType,
        ...(fields.businessType === 'OTHER' ? { businessTypeOther: fields.businessTypeOther.trim() } : {}),
        ...(fields.website ? { website: fields.website } : {}),
      }, { 'Idempotency-Key': requestKey.current })
      requestKey.current = crypto.randomUUID()
      setSent(true)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'No pudimos enviar tu solicitud. Revisa tu conexión e intenta de nuevo.')
    } finally {
      setSending(false)
    }
  }

  const field = (name: 'fullName' | 'email' | 'phone' | 'businessTypeOther', label: string, props: { type?: string; autoComplete?: string; inputMode?: 'tel' | 'email' | 'text'; maxLength: number }) =>
    <div className="form-field">
      <label htmlFor={`quote-${name}`} className="sr-only">{label}</label>
      <input id={`quote-${name}`} placeholder={label} type={props.type ?? 'text'} autoComplete={props.autoComplete} inputMode={props.inputMode} maxLength={props.maxLength}
        value={fields[name]} onChange={(event) => change(name, event.target.value)} disabled={sending}
        aria-invalid={Boolean(found[name])} aria-describedby={found[name] ? `quote-${name}-error` : undefined} />
      {found[name] && <p id={`quote-${name}-error`} className="field-error" role="alert">{found[name]}</p>}
    </div>

  return <section className="quote-card" aria-labelledby={titleId}>
    <header className="quote-card-head">
      <h2 id={titleId}>Cotiza para tu negocio</h2>
      <p>Déjanos tus datos y te contactamos por teléfono o correo con una cotización hecha para tu giro.</p>
    </header>
    {sent
      ? <div className="quote-card-body"><Notice tone="success"><strong>Recibimos tu solicitud.</strong> Te contactaremos pronto por teléfono o correo.</Notice>
        <Button intent="neutral" emphasis="ghost" onClick={another}>Enviar otra solicitud</Button></div>
      : <form ref={formRef} noValidate onSubmit={(event) => void submit(event)} className="quote-card-body quote-form">
        {field('fullName', 'Nombre completo', { autoComplete: 'name', maxLength: 160 })}
        {field('email', 'Correo electrónico', { type: 'email', autoComplete: 'email', inputMode: 'email', maxLength: 255 })}
        {field('phone', 'Teléfono', { type: 'tel', autoComplete: 'tel', inputMode: 'tel', maxLength: 30 })}
        <div className="form-field">
          <NativeSelect label="Giro" id="quote-businessType" data-empty={!fields.businessType || undefined} value={fields.businessType} disabled={sending} onChange={(event) => change('businessType', event.target.value)}
            aria-invalid={Boolean(found.businessType)} aria-describedby={found.businessType ? 'quote-businessType-error' : undefined}>
            <option value="">Elige tu giro</option>
            {quoteBusinessTypes.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </NativeSelect>
          {found.businessType && <p id="quote-businessType-error" className="field-error" role="alert">{found.businessType}</p>}
        </div>
        {fields.businessType === 'OTHER' && field('businessTypeOther', '¿Cuál es tu giro?', { autoComplete: 'off', maxLength: 80 })}
        {/* Left empty by people: the field is off screen and out of the tab order; a robot that fills it is ignored. */}
        <div className="quote-trap" aria-hidden="true"><label htmlFor="quote-website">Sitio web</label><input id="quote-website" tabIndex={-1} autoComplete="off" value={fields.website} onChange={(event) => change('website', event.target.value)} /></div>
        {error && <Notice>{error}</Notice>}
        <Button type="submit" className="quote-submit" busy={sending}>Cotizar</Button>
        <p className="field-hint quote-consent">Al enviar la solicitud aceptas nuestro <Link to="/aviso-de-privacidad" className="font-semibold text-[color:var(--ui-primary)] underline underline-offset-2">Aviso de privacidad</Link>.</p>
      </form>}
  </section>
}
